/**
 * Curated QueryBuilder field catalog for delivery-step conditions (shared by the
 * Track Status and Text Notes tabs — run_when and preconditions builders).
 *
 * Unlike the calling/validation builders (which derive fields from the CMM input
 * unique-values document), delivery steps gate on a small, fixed set of pipeline
 * status fields. We hardcode those fields, their enum value options, and a
 * delivery-specific operator set so the builder operator values match the
 * Firestore `run_when` operators 1:1 (`in`, `not_in`, `is_empty`, `stale_in`, …)
 * and round-trip with no operator translation table.
 */
import {
  FieldDefinition,
  OperatorDefinition,
  QueryBuilderConfig,
  QueryBuilderOption,
} from "../../../../../data-model/queryBuilder";

const toOptions = (values: string[]): QueryBuilderOption[] =>
  values.map((value) => ({ value, label: value }));

/**
 * Operator set for a delivery-step status field. Operator `value`s intentionally
 * mirror the Firestore `run_when` vocabulary so no mapping is needed on save.
 *
 * @param valueOptions - Enum options shown in multi/single-select value inputs
 * @returns Operator definitions for the field
 */
function statusOperators(
  valueOptions: QueryBuilderOption[],
): OperatorDefinition[] {
  return [
    {
      value: "in",
      label: "is any of",
      valueInputType: "multi-select",
      valueOptions,
    },
    {
      value: "not_in",
      label: "is not any of",
      valueInputType: "multi-select",
      valueOptions,
    },
    {
      value: "stale_in",
      label: "becomes stale in",
      valueInputType: "multi-select",
      valueOptions,
    },
    {
      value: "equals",
      label: "=",
      valueInputType: "single-select",
      valueOptions,
    },
    {
      value: "not_equals",
      label: "!=",
      valueInputType: "single-select",
      valueOptions,
    },
    { value: "is_empty", label: "is empty", valueInputType: "none" },
    { value: "is_not_empty", label: "is not empty", valueInputType: "none" },
  ];
}

/**
 * Builds a delivery-step field definition.
 *
 * @param value - Firestore column/field name (e.g. `meta.cmm_st_rpa_status`)
 * @param label - Human label for the field dropdown
 * @param enumValues - Known enum values for the field
 * @returns Field definition for the QueryBuilder config
 */
function field(
  value: string,
  label: string,
  enumValues: string[],
): FieldDefinition {
  const valueOptions = toOptions(enumValues);
  return {
    value,
    label,
    dataType: "STRING",
    operators: statusOperators(valueOptions),
    valueOptions,
  };
}

/** Curated condition-field config for delivery-step run_when / precondition builders. */
export const DELIVERY_STEP_FIELD_CONFIG: QueryBuilderConfig = {
  fields: [
    field("meta.cmm_st_rpa_status", "Track Status", [
      "success",
      "completed",
      "invalid_key",
      "failed",
      "patient_mismatch",
      "additional_qa",
      "stp_failed",
      "duplicate_key",
      "expired",
      "in_progress",
      "initiated",
    ]),
    field("response_status", "PA Response Status", [
      "Approved",
      "Auth Not Required",
      "Approval on File",
      "Denied",
      "Denial on File",
      "Drug Not Covered",
      "Pending",
      "Onshore Assistance",
      "Send To Client",
    ]),
    field("sftp_status", "SFTP Status", ["success", "resubmit", "re-submit"]),
    field("text_note_status", "Text Note Status", [
      "started",
      "in_progress",
      "failed",
      "not_applicable",
      "na",
      "error",
    ]),
    field("meta.denial_letter_upload_status", "Letter Upload Status", [
      "started",
      "in_progress",
      "initiated",
      "error",
      "na",
      "completed",
    ]),
    field("second_stp_status", "Second STP Status", ["sent_to_plan"]),
    field("stats_storage_status", "Stats Storage Status", [
      "success",
      "failed",
    ]),
  ],
};
